/*
  DisplayProofPage.jsx
  ----------------
  Interface pour afficher la preuve d'une attestation.
*/

import { useEffect, useState } from 'react';
import {
  Badge,
  Card,
  Center,
  Divider,
  Group,
  Stack,
  Text,
  Loader,
  Table,
  Button,
  Box,
  rem,
  Anchor,
  Modal,
  CopyButton,
  Tooltip,
  ActionIcon,
  PasswordInput,
  TextInput
} from '@mantine/core';
import { IconShare, IconCopy, IconCheck, IconLock, IconFile, IconFileTypePdf, IconFileText, IconFileSpreadsheet } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { useLocation } from 'react-router-dom';
import { get } from '../tools/Request';
import DisplayFlow from '../tools/DisplayFlow';
import { useCallback } from 'react';

const hostaddress = process.env.REACT_APP_API_URL;
const BLOCKCHAIN_EXPLORER_TX = process.env.REACT_APP_BLOCKCHAIN_EXPLORER_TX;

export default function DisplayProofPage({ setUid }) {
  const location = useLocation();
  const uid = setUid || new URLSearchParams(location.search).get('uid');
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fileData, setFileData] = useState(null);
  const [connections, setConnections] = useState([]);
  const [linkedIds, setLinkedIds] = useState([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [decryptionKey, setDecryptionKey] = useState('');

  const renderValue = (value) => {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return value;
  };

  const getFileIcon = (file) => {
    // Handle both with/without dot: .png or png
    const fileExt = file.fileExtension?.toLowerCase().replace(/^\./, '');
    const fileName = file.fileName?.toLowerCase();

    // Check if it's an image by extension or filename
    const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(fileExt) || fileName?.match(/\.(png|jpg|jpeg|webp|gif|bmp|svg)$/i);

    if (isImage) {
      return (
        <img src={`${hostaddress}api/tools/${file.fileHash}?uid=${uid}&inline=1`} alt={file.fileName} style={{ width: 85, height: 85, objectFit: 'cover', borderRadius: 8 }} />
      );
    }
    if (fileExt === 'pdf') return <IconFileTypePdf size={32} color="red" />;
    if (fileExt?.match(/^(doc|docx|txt)$/)) return <IconFileText size={32} color="blue" />;
    if (fileExt?.match(/^(xls|xlsx|csv)$/)) return <IconFileSpreadsheet size={32} color="green" />;
    return <IconFile size={32} />;
  };

  const fetchProof = useCallback(
    async (key = '') => {
      try {
        if (!uid) throw new Error('UID manquant.');

        const response = await get('proof', { uid, key, user_uid: localStorage.getItem('userToken') }, localStorage.getItem('userToken'));
        const linkFlow = await get('link/link-flow', { uid }, localStorage.getItem('userToken'));

        setConnections(linkFlow.data.connections);
        setLinkedIds(linkFlow.data.linkedIds);
        setProof(response);
        if (response.type === 'file') setFileData(response.entry.data);
      } catch (e) {
        const errorMessage = e.response?.data?.message || e.message || 'Une erreur est survenue.';
        showNotification({
          title: 'Erreur',
          message: errorMessage,
          color: 'red',
          autoClose: 3000
        });
      } finally {
        setLoading(false);
      }
    },
    [uid]
  );

  useEffect(() => {
    fetchProof();
  }, [fetchProof]);

  const handleDecrypt = async () => {
    if (!decryptionKey) {
      showNotification({
        title: 'Erreur',
        message: 'Veuillez entrer une clé de déchiffrement.',
        color: 'red',
        autoClose: 3000
      });
      return;
    }
    await fetchProof(decryptionKey);
  };

  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  if (!proof) {
    return (
      <Center style={{ height: '100vh' }}>
        <Text size="lg">Aucune données pour l'UID: {uid}</Text>
      </Center>
    );
  }

  const { entry, type } = proof;
  const formattedDate = new Date(parseInt(entry.date)).toLocaleString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const shareLink = `${window.location.origin}/proof?uid=${uid}`;

  const renderData = () => {
    if (type === 'data') {
      const isSimple = !Array.isArray(entry.data) && !Object.keys(entry.data).some((key) => !isNaN(key));

      if (isSimple) {
        const keys = Object.keys(entry.data);
        const values = Object.values(entry.data);
        return (
          <Table striped highlightOnHover withBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                {keys.map((key, i) => (
                  <Table.Th key={i}>{key}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <tbody>
              <Table.Tr>
                {values.map((val, i) => (
                  <Table.Td key={i}>{renderValue(val)}</Table.Td>
                ))}
              </Table.Tr>
            </tbody>
          </Table>
        );
      }

      const rows = Object.values(entry.data);
      const columns = Object.keys(rows[0] || {});
      return (
        <Table striped highlightOnHover withBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              {columns.map((col, i) => (
                <Table.Th key={i}>{col}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <tbody>
            {rows.map((item, i) => (
              <Table.Tr key={i}>
                {columns.map((col, j) => (
                  <Table.Td key={j}>{typeof item[col] === 'object' ? JSON.stringify(item[col]) : renderValue(item[col])}</Table.Td>
                ))}
              </Table.Tr>
            ))}
          </tbody>
        </Table>
      );
    }

    if (type === 'file' && fileData) {
      return (
        <Table striped highlightOnHover withBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Aperçu</Table.Th>
              <Table.Th>Nom</Table.Th>
              <Table.Th>Hash du fichier</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Action</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <tbody>
            {fileData.map((file, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getFileIcon(file)}</Box>
                </Table.Td>
                <Table.Td>{file.fileName}</Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Text>{file.fileHash.slice(0, 10)}...</Text>
                    <CopyButton value={file.fileHash}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copié!' : 'Copier'} withArrow>
                          <ActionIcon color={copied ? 'teal' : 'blue'} onClick={copy} size="sm">
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Table.Td>
                <Table.Td>{file.fileExtension}</Table.Td>
                <Table.Td>
                  <Button size="xs" onClick={() => window.open(`${hostaddress}api/tools/${file.fileHash}?uid=${uid}`, '_blank')}>
                    Télécharger
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </tbody>
        </Table>
      );
    }

    return (
      <Text color="red" size="sm">
        Données invalides ou manquantes.
      </Text>
    );
  };

  return (
    <Center>
      <Card mt="5%" w={setUid ? '100%' : '75%'} shadow="lg" padding="xl" radius="md" withBorder>
        <Stack>
          {/* Header */}
          <Group justify="space-between">
            <Group>
              <IconLock style={{ width: rem(35), height: rem(35) }} />
              <Text ml={-10} fw={700} size="lg">
                Preuve d'authenticité
              </Text>
            </Group>
            <Group gap="sm">
              {proof.revoked ? (
                <Badge color="red" size="lg">
                  Attestation révoquée
                </Badge>
              ) : (
                <Badge color="green" size="lg">
                  Attestation valide
                </Badge>
              )}
              {!proof.decrypted && !fileData && proof.encrypted && (
                <>
                  <PasswordInput value={decryptionKey} onChange={(e) => setDecryptionKey(e.target.value)} placeholder="Clé de déchiffrement" w={200} />
                  <Button size="xs" variant="light" onClick={handleDecrypt}>
                    Déchiffrer
                  </Button>
                </>
              )}
              <ActionIcon variant="light" color="blue" onClick={() => setShareModalOpen(true)} title="Partager">
                <IconShare size={20} />
              </ActionIcon>
            </Group>
          </Group>

          {/* Meta */}
          <Divider />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              <strong>Date :</strong> {formattedDate}
            </Text>
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                <strong>UID :</strong> {uid}
              </Text>
              <CopyButton value={uid}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copié!' : 'Copier'} withArrow>
                    <ActionIcon color={copied ? 'teal' : 'blue'} onClick={copy} size="sm" variant="light">
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Group>

          {/* Data Section */}
          <Divider />
          <Text fw={700} size="md">
            Données
          </Text>
          {renderData()}

          {/* Blockchain Info */}
          <Divider />
          <Text fw={700} size="md">
            Preuve Blockchain
          </Text>
          {!entry.blockhash ? (
            <Group align="center" mt="sm">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Attestation en cours
              </Text>
            </Group>
          ) : (
            <Stack spacing={2}>
              <Text size="sm">
                <strong>Block Hash :</strong> {entry.blockhash}
              </Text>
              <Text size="sm">
                <strong>Block Number :</strong> {entry.blocknumber}
              </Text>
              <Text size="sm">
                <strong>Transaction :</strong>{' '}
                <Anchor href={`${BLOCKCHAIN_EXPLORER_TX}${entry.transactionhash}`} target="_blank" rel="noopener noreferrer">
                  {entry.transactionhash}
                </Anchor>
              </Text>
              <Text size="sm">
                <strong>Date d'insertion : </strong>
                {new Date(parseInt(entry.blockchaindate)).toLocaleString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Text>
            </Stack>
          )}

          {/* Graph */}
          {linkedIds.length > 0 && connections.length > 0 && !setUid && (
            <Card mt="lg" w="100%" shadow="md" radius="md" withBorder>
              <Box h="70vh">
                <DisplayFlow connections={connections} linkedIds={linkedIds} />
              </Box>
            </Card>
          )}
        </Stack>

        {/* Modal */}
        <Modal opened={shareModalOpen} onClose={() => setShareModalOpen(false)} title="Partager la preuve" centered size="lg">
          <Box p="sm">
            <Stack spacing="sm">
              <Text size="sm" c="dimmed">
                Ce lien permet à d'autres utilisateurs de consulter cette preuve.
              </Text>
              <CopyButton value={shareLink}>
                {({ copied, copy }) => (
                  <Group gap="xs" align="flex-end">
                    <Box style={{ flex: 1 }}>
                      <Text size="sm" fw={500} mb={4}>
                        Lien de partage
                      </Text>
                      <TextInput value={shareLink} readOnly variant="filled" radius="md" size="sm" />
                    </Box>
                    <Tooltip label={copied ? 'Copié!' : 'Copier'} withArrow>
                      <ActionIcon color={copied ? 'teal' : 'blue'} onClick={copy} size="lg" variant="light">
                        {copied ? <IconCheck size={20} /> : <IconCopy size={20} />}
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
              </CopyButton>
            </Stack>
          </Box>
        </Modal>
      </Card>
    </Center>
  );
}
